export type ResourceType =
  | "video"
  | "article"
  | "guide"
  | "form"
  | "template"
  | "call";

export type ResourceCategory =
  | "Getting started"
  | "Product"
  | "Pricing & ROI"
  | "Intake & Planning"
  | "Communications & Marketing"
  | "Reference";

export interface Resource {
  id: string;
  title: string;
  description: string;
  type: ResourceType;
  category: ResourceCategory;
  url: string;
  /** e.g. "3 min watch", "5 min read" */
  duration?: string;
}

export const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  video: "Video",
  article: "Article",
  guide: "Guide",
  form: "Form",
  template: "Template",
  call: "Talk to us",
};

const TOOLKIT_BASE =
  "https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/toolkit";

// Featured order — the default sort in the drawer.
export const RESOURCES: Resource[] = [
  {
    id: "learning-center",
    title: "Learning Center",
    description:
      "Self-paced courses and estate planning scenarios, with a certificate at the end.",
    type: "guide",
    category: "Getting started",
    url: "https://content.buildxcl.com/?m=40b363dc-b030-11f0-9c7a-5abc8ea51e38&n=ddc26ccb-f6b7-11f0-b847-7ec8010dfd8b",
  },
  {
    id: "pricing-page",
    title: "Plans and pricing",
    description: "Full pricing details for every tier, side by side.",
    type: "article",
    category: "Pricing & ROI",
    url: "https://www.getsnug.com/pricing",
    duration: "2 min read",
  },
  {
    id: "client-organization",
    title: "Help clients get organized with Total Estate Planning",
    description:
      "How advisors use Snug to bring a client's full estate picture into one place.",
    type: "article",
    category: "Getting started",
    url: "https://www.getsnug.com/post/help-clients-get-organized-with-total-estate-planning",
    duration: "5 min read",
  },
  {
    id: "estate-planning-faqs",
    title: "Estate Planning FAQs",
    description:
      "Legal-approved answers to common client questions, helping you steer clear of the unauthorized practice of law.",
    type: "guide",
    category: "Communications & Marketing",
    url: `${TOOLKIT_BASE}/Estate+Planning+FAQs.pdf`,
  },
  {
    id: "preparing-for-estate-planning",
    title: "Preparing for Estate Planning",
    description:
      "Worksheet and guide covering every decision a client makes when creating their estate plan.",
    type: "guide",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Estate+Planning+Preparation+-+Worksheet+%26+Guide.pdf`,
  },
  {
    id: "individual-vs-joint-trust",
    title: "Individual vs. Joint Trust Guide",
    description:
      "Helps couples understand and decide between Individual and Joint Trusts as the foundation of their estate plan.",
    type: "guide",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Individual+vs+Joint+Trust+Guide.pdf`,
  },
  {
    id: "trust-funding-guide",
    title: "Trust Funding General Guide",
    description:
      "The process and considerations for moving bank accounts, investments, real estate, and other assets into a Trust.",
    type: "guide",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Trust+Funding+General+Guide.pdf`,
  },
  {
    id: "intake-living-trust",
    title: "Intake Form - Revocable Living Trust",
    description:
      "Pen-and-paper intake form to collect everything needed to create a Revocable Living Trust.",
    type: "form",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Intake+Form+-+Living+Trust.pdf`,
  },
  {
    id: "intake-will",
    title: "Intake Form - Last Will & Testament",
    description:
      "Pen-and-paper intake form to collect everything needed to create a Will.",
    type: "form",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Intake+Form+-+Will.pdf`,
  },
  {
    id: "intake-hcd",
    title: "Intake Form - Health Care Directive",
    description:
      "Pen-and-paper intake form to collect everything needed to create a Health Care Directive.",
    type: "form",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Intake+Form+-+Health+Care+Directive.pdf`,
  },
  {
    id: "intake-fpoa",
    title: "Intake Form - Financial Power of Attorney",
    description:
      "Pen-and-paper intake form to collect everything needed to create a Financial Power of Attorney.",
    type: "form",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Intake+Form+-+Financial+Power+of+Attorney.pdf`,
  },
  {
    id: "intake-consent",
    title: "Consent for Intake Form Use",
    description:
      "Client consent form authorizing the use of intake forms for estate planning purposes.",
    type: "form",
    category: "Intake & Planning",
    url: `${TOOLKIT_BASE}/Consent+for+Intake+Form+Use.pdf`,
  },
  {
    id: "client-email-templates",
    title: "Client Email Templates",
    description:
      "Sample email marketing templates — a good starting point for the value proposition and pitch to clients.",
    type: "template",
    category: "Communications & Marketing",
    url: `${TOOLKIT_BASE}/Client+Email+Templates.zip`,
  },
  {
    id: "postcard-high-touch",
    title: "Postcard (High Touch)",
    description:
      "Postcard template for high-touch engagement campaigns that drive estate planning conversations.",
    type: "template",
    category: "Communications & Marketing",
    url: `${TOOLKIT_BASE}/Whitelabeled+Postcard+-+High+Touch.pdf`,
  },
  {
    id: "postcard-low-touch",
    title: "Postcard (Low Touch)",
    description:
      "Postcard template for low-touch automated outreach that scales estate planning awareness.",
    type: "template",
    category: "Communications & Marketing",
    url: `${TOOLKIT_BASE}/Whitelabeled+Postcard+-+Low+Touch.pdf`,
  },
  {
    id: "upl-guide",
    title: "Guide to Unauthorized Practice of Law",
    description:
      "How to avoid the unauthorized practice of law (UPL) and how the platform is designed to support that.",
    type: "guide",
    category: "Reference",
    url: `${TOOLKIT_BASE}/Guide+to+Avoiding+Unauthorized+Practice+of+Law+(UPL).pdf`,
  },
];
